import { BreakpointObserver } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { Subject, map } from 'rxjs';
import {
  AssignmentsStepResult,
  ClientTimingStepResult,
  DescribeStepResult,
  ResourcesStepResult,
  WizardResult,
} from '../../../models/request-wizard.models';
import { AssignmentsStepComponent } from './steps/assignments-step.component';
import { ClientTimingStepComponent } from './steps/client-timing-step.component';
import { DescribeStepComponent } from './steps/describe-step.component';
import { ResourcesStepComponent } from './steps/resources-step.component';

@Component({
  selector: 'app-request-wizard-body',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [
    CommonModule,
    MatStepperModule,
    MatButtonModule,
    MatIconModule,
    DescribeStepComponent,
    ClientTimingStepComponent,
    ResourcesStepComponent,
    AssignmentsStepComponent,
  ],
  styleUrls: ['./request-wizard-body.component.scss'],
  templateUrl: './request-wizard-body.component.html',
})
export class RequestWizardBodyComponent implements OnInit, AfterViewInit, OnDestroy {
  private breakpointObserver = inject(BreakpointObserver);
  private cdr = inject(ChangeDetectorRef);

  @Input() mode: 'create' | 'edit' = 'create';
  @Input() initial: WizardResult | null = null;
  @Input() isSubmitting = false;
  @Output() submitWizard = new EventEmitter<WizardResult>();
  @Output() cancel = new EventEmitter<void>();

  @ViewChild('stepper') stepper?: MatStepper;
  @ViewChild('describeRef') describeRef?: DescribeStepComponent;
  @ViewChild('clientTimingRef') clientTimingRef?: ClientTimingStepComponent;
  @ViewChild('resourcesRef') resourcesRef?: ResourcesStepComponent;
  @ViewChild('assignmentsRef') assignmentsRef?: AssignmentsStepComponent;

  describeResult: DescribeStepResult | null = null;
  clientTimingResult: ClientTimingStepResult | null = null;
  resourcesResult: ResourcesStepResult | null = null;
  assignmentsResult: AssignmentsStepResult | null = null;

  readonly orientation = toSignal(
    this.breakpointObserver
      .observe(['(max-width: 720px)'])
      .pipe(
        map((s) =>
          s.matches ? ('vertical' as const) : ('horizontal' as const),
        ),
      ),
    { initialValue: 'horizontal' as const },
  );

  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    if (this.initial) {
      this.describeResult = this.initial.describe;
      this.clientTimingResult = this.initial.clientTiming;
      this.resourcesResult = this.initial.resources;
      this.assignmentsResult = this.initial.assignments;
    }
  }

  ngAfterViewInit(): void {
    // @ViewChild refs (e.g. clientTimingRef.form) are not available during the
    // first change-detection pass, so step1Valid reads as false then immediately
    // becomes true when the child's form initialises — causing NG0100. A single
    // detectChanges() after the view is fully initialised resolves this.
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get stepIndex(): number {
    return this.stepper?.selectedIndex ?? 0;
  }

  get step0Valid(): boolean {
    return !!this.describeRef?.form?.valid;
  }
  get step1Valid(): boolean {
    return !!this.clientTimingRef?.form?.valid;
  }
  /** Resources step: requested counts + preferred resources (both optional). */
  get step2Valid(): boolean {
    return this.resourcesRef ? !!this.resourcesRef.form?.valid : true;
  }
  /** Assignments step: per-matter initial assignments (optional). */
  get step3Valid(): boolean {
    return this.assignmentsRef ? !!this.assignmentsRef.form?.valid : true;
  }

  next(): void {
    this.stepper?.next();
  }
  back(): void {
    this.stepper?.previous();
  }

  canSubmit(): boolean {
    return (
      this.step0Valid && this.step1Valid && this.step2Valid && this.step3Valid
    );
  }

  /** Aggregated, human-readable list of what still needs completing to submit. */
  getValidationIssues(): string[] {
    return [
      ...(this.describeRef?.getValidationIssues() ?? []),
      ...(this.clientTimingRef?.getValidationIssues() ?? []),
    ];
  }

  /**
   * Marks every step's form as touched so fields with validation errors reveal
   * their error state — Angular Material only surfaces a control's error once it
   * is touched (or its form submitted). Called when a submit is attempted while
   * issues remain, so the offending fields turn red behind the errors dialog.
   */
  revealValidationErrors(): void {
    this.describeRef?.form?.markAllAsTouched();
    this.clientTimingRef?.form?.markAllAsTouched();
    this.resourcesRef?.form?.markAllAsTouched();
    this.assignmentsRef?.form?.markAllAsTouched();
  }

  hasUnsavedChanges(): boolean {
    return !!(
      this.describeRef?.form?.dirty ||
      this.clientTimingRef?.form?.dirty ||
      this.resourcesRef?.form?.dirty ||
      this.assignmentsRef?.form?.dirty
    );
  }

  onSubmit(): void {
    if (!this.canSubmit()) return;
    this.submitWizard.emit({
      describe: this.describeResult!,
      clientTiming: this.clientTimingResult!,
      resources: this.resourcesResult!,
      assignments: this.assignmentsResult!,
    });
  }
}
